// /admin/v2/links — Question links review (BRIEFS C7.1, SPEC §5 + §7). Every question of a
// chapter should point at the exact notes point that holds its answer. The AI proposes a
// point (with a confidence); the admin confirms it, changes it (point picker with search)
// or says "No matching point". Harsh's "Read this in your notes" uses the confirmed point,
// else a proposal of 80% or more, else the unit of the question's module, else the chapter.
// C11.13: on the test copy the server links released chapters by itself (AutoPanel).
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  v2AdminChapters, v2Links, v2LinksPropose, v2LinksJobs, v2LinksAuto, v2LinkConfirm, v2LinkNoMatch,
  v2LinksConfirmAll, v2LinkPoints, errText,
} from '../../../api/v2admin';
import V2Tabs, { Msg, chapterLabel } from './V2Tabs';

const muted = { fontSize: 12, color: 'var(--text-muted)' };
const pct = (c) => (c === null || c === undefined ? '—' : `${Math.round(c * 100)}%`);

const FILTERS = [
  { key: 'unconfirmed', label: 'Not confirmed' },
  { key: 'low', label: 'Low confidence' },
  { key: 'none', label: 'No proposal' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'no_match', label: 'No matching point' },
  { key: 'all', label: 'All' },
];

const STATE = {
  confirmed: { text: 'Confirmed', cls: 'badge-green' },
  proposed: { text: 'Proposed', cls: 'badge-yellow' },
  none: { text: 'No proposal', cls: 'badge-blue' },
  no_match: { text: 'No matching point', cls: 'badge-red' },
};

function ConfidenceTag({ value, low }) {
  const weak = value === null || value === undefined || value < low;
  return (
    <span style={{
      fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
      background: weak ? '#FEF2F2' : '#D1FAE5', color: weak ? '#B91C1C' : '#047857',
    }}>{pct(value)} sure{weak ? ' · low' : ''}</span>
  );
}

function PointBox({ link, label }) {
  if (!link) return null;
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', background: '#F8FAFC' }}>
      <div style={muted}>{label} · <strong>{link.point_code}</strong> · {link.unit_number ? `${link.unit_number} ` : ''}{link.unit_title}</div>
      <div style={{ fontSize: 14, marginTop: 2 }}>{link.text}</div>
    </div>
  );
}

// ------------------------------------------------------------------ point picker (Change)

function PointPicker({ chapterId, onPick, onCancel }) {
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const { data, isFetching } = useQuery({
    queryKey: ['v2-link-points', chapterId, term],
    queryFn: () => v2LinkPoints(chapterId, term),
    enabled: term.length >= 2,
  });
  const results = data ? data.points : [];
  return (
    <div data-testid="v2-link-picker" style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="form-input" autoFocus data-testid="v2-link-point-search" value={q}
          onChange={(e) => setQ(e.target.value)} style={{ flexGrow: 1 }}
          placeholder="Search the notes of this chapter: words of the point or its code (e.g. Meselson, XII-05.2)" />
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      </div>
      {term.length >= 2 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginTop: 4, maxHeight: 280, overflowY: 'auto', background: 'var(--surface)' }}>
          {isFetching && !data && <div style={{ padding: 10, ...muted }}>Searching…</div>}
          {data && results.length === 0 && <div style={{ padding: 10, ...muted }}>No point found.</div>}
          {results.map((p) => (
            <button key={p.point_code} type="button" data-testid={`v2-link-pick-${p.point_code}`}
              onClick={() => onPick(p.point_code)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 0,
                borderBottom: '1px solid var(--border)', background: 'transparent', cursor: 'pointer', fontSize: 14,
              }}>
              <div style={muted}><strong>{p.point_code}</strong> · {p.unit_number ? `${p.unit_number} ` : ''}{p.unit_title}</div>
              {p.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ one question

function QuestionCard({ row, chapterId, low, onChanged, onError }) {
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const st = STATE[row.state] || STATE.none;

  const run = async (fn, okText) => {
    setBusy(true);
    try { const out = await fn(); setPicking(false); onChanged(out, okText); } catch (x) { onError(errText(x)); } finally { setBusy(false); }
  };
  const confirm = (code) => run(() => v2LinkConfirm(row.question_id, code), `Question ${row.question_id} → ${code} confirmed.`);
  const noMatch = () => run(() => v2LinkNoMatch(row.question_id), `Question ${row.question_id}: no matching point.`);

  return (
    <div className="card" data-testid={`v2-link-q-${row.question_id}`} style={{ marginBottom: 12, padding: 16 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 6 }}>
        <strong>Q {row.question_id}</strong>
        <span className={`badge ${st.cls}`}>{st.text}</span>
        <span style={muted}>{row.question_type}{row.topic ? ` · ${row.topic}` : ''}{row.module_code ? ` · ${row.module_code}` : ''}</span>
      </div>
      {row.passage && <div style={{ ...muted, marginBottom: 4 }}>Passage: {row.passage}</div>}
      <div style={{ fontSize: 15, marginBottom: 6 }}>{row.question}</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 4, fontSize: 14, marginBottom: 10 }}>
        {['a', 'b', 'c', 'd'].map((k) => (
          <div key={k} style={{ color: k === row.correct_option ? '#047857' : undefined, fontWeight: k === row.correct_option ? 600 : 400 }}>
            ({k}) {row.options[k]}{k === row.correct_option ? '  ✓ correct' : ''}
          </div>
        ))}
      </div>

      {row.confirmed && <PointBox link={row.confirmed} label="Confirmed point" />}
      {!row.confirmed && row.proposed && (
        <div>
          <PointBox link={row.proposed} label="Proposed point" />
          <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
            <ConfidenceTag value={row.proposed.confidence} low={low} />
            {row.other_proposals > 0 && <span style={muted}>+ {row.other_proposals} weaker proposal{row.other_proposals > 1 ? 's' : ''}</span>}
          </div>
        </div>
      )}
      {!row.confirmed && !row.proposed && (
        <div style={muted}>{row.no_match ? 'You said no point of the notes holds this answer.' : 'No point proposed yet.'}</div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        {!row.confirmed && row.proposed && (
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} data-testid={`v2-link-confirm-${row.question_id}`}
            onClick={() => confirm(row.proposed.point_code)}>Confirm</button>
        )}
        <button type="button" className="btn btn-ghost btn-sm" disabled={busy} data-testid={`v2-link-change-${row.question_id}`}
          onClick={() => setPicking(!picking)}>{row.confirmed || row.proposed ? 'Change' : 'Choose a point'}</button>
        {row.state !== 'no_match' && (
          <button type="button" className="btn btn-ghost btn-sm" disabled={busy} data-testid={`v2-link-nomatch-${row.question_id}`}
            onClick={noMatch}>No matching point</button>
        )}
      </div>
      {picking && <PointPicker chapterId={chapterId} onPick={confirm} onCancel={() => setPicking(false)} />}
    </div>
  );
}

// ------------------------------------------------------------------ AI proposals

const isAuto = (job) => !!(job && job.details && job.details.auto);
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
function runCounts(d) {
  if (!d || d.added === undefined) return '';
  let t = ` · ${d.added} new, ${d.updated} updated, ${d.no_point} without a point, ${d.invalid} bad answers`;
  if (d.confirmed !== undefined) t += ` · ${d.confirmed} confirmed by the server (${Math.round((d.min_confidence || 0.9) * 100)}% or more)`;
  return t;
}
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');

// C11.13 — automatic linking on the test copy: on/off, and where this chapter stands.
function AutoPanel({ chapterId }) {
  const auto = useQuery({ queryKey: ['v2-link-auto'], queryFn: v2LinksAuto, refetchInterval: 30000 });
  const a = auto.data;
  if (!a) return null;
  const mine = a.chapters.find((c) => String(c.chapter_id) === String(chapterId));
  const last = mine && mine.last_run;
  return (
    <div className="card" style={{ marginBottom: 20, borderLeft: `4px solid ${a.enabled ? '#047857' : '#94A3B8'}` }} data-testid="v2-link-auto">
      <h2 style={{ fontSize: 18, marginBottom: 6 }}>
        Automatic linking: <span style={{ color: a.enabled ? '#047857' : '#475569' }} data-testid="v2-link-auto-state">{a.enabled ? 'ON' : 'OFF'}</span>
      </h2>
      {a.enabled ? (
        <div style={{ fontSize: 14, marginBottom: 6 }}>
          Every {a.every_minutes} minutes the server takes one released chapter whose questions have no proposal yet, asks the AI,
          and confirms proposals of {Math.round(a.min_confidence * 100)}% or more by itself. You can still change any of them below.
        </div>
      ) : (
        <div style={{ ...muted, fontSize: 14 }}>{a.reason}</div>
      )}
      {a.enabled && (
        <div style={{ fontSize: 14 }} data-testid="v2-link-auto-chapter">
          {!mine ? 'This chapter is not released (or has no notes yet), so it is not linked automatically.' : (
            <>
              <div><strong>This chapter:</strong> {plural(mine.waiting, 'question', 'questions')} without a proposal. {mine.due ? 'Waiting for its turn.' : ''} <span style={muted}>{mine.why}</span></div>
              {last && (
                <div style={{ marginTop: 4 }}>
                  {last.running ? `Automatic run working… ${last.done} of ${last.total} questions done.`
                    : `Last automatic run ${when(last.finished_at)}: ${last.done} of ${last.total} questions${runCounts(last.details)}${last.details && last.details.left !== undefined ? ` · ${plural(last.details.left, 'question', 'questions')} still without a proposal` : ''}${last.error ? ` · ${last.error}` : ''}`}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ProposalPanel({ chapterId, modelOk, onFinished, setMsg }) {
  const [limit, setLimit] = useState('');
  const [redo, setRedo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const jobs = useQuery({
    queryKey: ['v2-link-jobs', chapterId],
    queryFn: () => v2LinksJobs(chapterId),
    enabled: !!chapterId,
    refetchInterval: (query) => {
      const d = query.state.data;
      return d && d.jobs.some((j) => j.running) ? 3000 : 30000;
    },
  });
  const last = jobs.data && jobs.data.jobs[0];
  const running = last && last.running;
  const [wasRunning, setWasRunning] = useState(false);
  useEffect(() => {
    if (running) setWasRunning(true);
    if (!running && wasRunning) { setWasRunning(false); onFinished(); }
  }, [running, wasRunning, onFinished]);

  const body = (dry) => ({ chapter_id: Number(chapterId), dry_run: dry, redo, ...(limit ? { limit: Number(limit) } : {}) });
  const tryIt = async () => {
    setBusy(true); setPreview(null);
    try { setPreview(await v2LinksPropose(body(true))); } catch (x) { setMsg({ kind: 'err', text: errText(x) }); } finally { setBusy(false); }
  };
  const start = async () => {
    setBusy(true);
    try {
      const out = await v2LinksPropose(body(false));
      setMsg({ kind: 'ok', text: `Started: ${out.job.total} question${out.job.total === 1 ? '' : 's'} to propose.` });
      jobs.refetch(); onFinished();
    } catch (x) { setMsg({ kind: 'err', text: errText(x) }); } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }} data-testid="v2-link-propose">
      <h2 style={{ fontSize: 18, marginBottom: 6 }}>Ask the AI for proposals</h2>
      <div style={{ ...muted, marginBottom: 10 }}>
        Only questions without a confirmed point (and not marked "No matching point") are sent. Questions that already have a proposal are skipped unless you tick "Ask again".
      </div>
      {!modelOk && <div role="alert" style={{ color: '#B91C1C', marginBottom: 8 }}>The AI key is not set on this server, so proposals cannot be made here.</div>}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={{ fontSize: 14 }}>How many questions (empty = all)&nbsp;
          <input className="form-input" type="number" min="1" value={limit} onChange={(e) => setLimit(e.target.value)} style={{ width: 100 }} data-testid="v2-link-limit" />
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14 }}>
          <input type="checkbox" checked={redo} onChange={(e) => setRedo(e.target.checked)} /> Ask again for proposed questions
        </label>
        <button type="button" className="btn btn-ghost" disabled={busy || !modelOk || running} onClick={tryIt} data-testid="v2-link-try">Try on up to 20 (nothing saved)</button>
        <button type="button" className="btn btn-primary" disabled={busy || !modelOk || running} onClick={start} data-testid="v2-link-start">Start proposals</button>
      </div>
      {last && (
        <div style={{ marginTop: 10, fontSize: 14 }} data-testid="v2-link-job">
          {running ? `${isAuto(last) ? 'Automatic run working' : 'Working'}… ${last.done} of ${last.total} questions done.`
            : `${isAuto(last) ? 'Last run (automatic)' : 'Last run'}: ${last.done} of ${last.total} questions${runCounts(last.details)}${last.error ? ` · Error: ${last.error}` : ''}`}
        </div>
      )}
      {preview && (
        <div style={{ marginTop: 12 }} data-testid="v2-link-preview">
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Trial: {preview.proposals.length} proposal{preview.proposals.length === 1 ? '' : 's'} for {preview.total} question{preview.total === 1 ? '' : 's'} ({preview.calls} AI call{preview.calls === 1 ? '' : 's'}, nothing saved)</div>
          <table className="table"><thead><tr><th>Question</th><th>Point</th><th>Sure</th></tr></thead>
            <tbody>{preview.proposals.map((p) => (
              <tr key={p.question_id}><td>Q {p.question_id}</td><td><strong>{p.point_code}</strong><div style={muted}>{p.point_text}</div></td><td>{pct(p.confidence)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ page

export default function AdminV2Links() {
  const [chapterId, setChapterId] = useState('');
  const [filter, setFilter] = useState('unconfirmed');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [msg, setMsg] = useState(null);

  const chapters = useQuery({ queryKey: ['v2-admin-chapters'], queryFn: v2AdminChapters });
  const chapterList = chapters.data ? (chapters.data.chapters || chapters.data) : [];
  useEffect(() => {
    if (!chapterId && Array.isArray(chapterList) && chapterList.length) {
      const withNotes = chapterList.find((c) => c.points > 0) || chapterList[0];
      setChapterId(String(withNotes.chapter_id));
    }
  }, [chapterId, chapterList]);

  const params = { chapter_id: chapterId, filter, page, ...(q.trim() ? { q: q.trim() } : {}) };
  const list = useQuery({ queryKey: ['v2-links', params], queryFn: () => v2Links(params), enabled: !!chapterId, placeholderData: (prev) => prev });
  const d = list.data;
  const refetch = list.refetch;

  const changed = (row, text) => { setMsg({ kind: 'ok', text }); refetch(); };
  const bulk = async () => {
    try {
      const out = await v2LinksConfirmAll(Number(chapterId), d.thresholds.bulk);
      setMsg({ kind: 'ok', text: `Confirmed ${out.confirmed} proposal${out.confirmed === 1 ? '' : 's'} of ${Math.round(out.min_confidence * 100)}% or more.` });
      refetch();
    } catch (x) { setMsg({ kind: 'err', text: errText(x) }); }
  };

  return (
    <div>
      <V2Tabs title="Question links"
        intro={'Each question should point at the exact notes point that holds its answer. Harsh\'s "Read this in your notes" opens the confirmed point; without one, a proposal of 80% or more; else the unit of the question\'s module; else the chapter.'} />
      <Msg msg={msg} />

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <select className="form-select" value={chapterId} onChange={(e) => { setChapterId(e.target.value); setPage(1); }} aria-label="Chapter" data-testid="v2-link-chapter">
          {Array.isArray(chapterList) && chapterList.map((c) => (
            <option key={c.chapter_id} value={c.chapter_id}>{chapterLabel(c)} ({c.points} points)</option>
          ))}
        </select>
      </div>

      {chapterId && <AutoPanel chapterId={chapterId} />}
      {chapterId && <ProposalPanel chapterId={chapterId} modelOk={d ? d.model_available : true} onFinished={refetch} setMsg={setMsg} />}

      {d && (
        <div className="card" style={{ marginBottom: 16 }} data-testid="v2-link-counts">
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 14, alignItems: 'center' }}>
            <span><strong>{d.counts.total}</strong> questions</span>
            <span style={{ color: '#047857' }}><strong>{d.counts.confirmed}</strong> confirmed</span>
            <span><strong>{d.counts.proposed}</strong> proposed ({d.counts.low} low)</span>
            <span><strong>{d.counts.none}</strong> no proposal</span>
            <span style={{ color: '#B91C1C' }}><strong>{d.counts.no_match}</strong> no matching point</span>
            <span style={{ flexGrow: 1 }} />
            <button type="button" className="btn btn-primary btn-sm" disabled={!d.counts.ready_bulk} onClick={bulk} data-testid="v2-link-bulk">
              Confirm all ≥ {Math.round(d.thresholds.bulk * 100)}% ({d.counts.ready_bulk})
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        {FILTERS.map((f) => (
          <button key={f.key} type="button" className={`btn btn-sm ${filter === f.key ? 'btn-primary' : 'btn-ghost'}`}
            data-testid={`v2-link-filter-${f.key}`} onClick={() => { setFilter(f.key); setPage(1); }}>{f.label}</button>
        ))}
        <input className="form-input" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
          placeholder="Search question text, id or point code" aria-label="Search questions" style={{ marginLeft: 'auto', minWidth: 260 }} />
      </div>

      {list.isLoading && <div className="spinner" />}
      {d && d.questions.length === 0 && <div className="card" style={muted}>No questions here.</div>}
      {d && d.questions.map((row) => (
        <QuestionCard key={row.question_id} row={row} chapterId={chapterId} low={d.thresholds.low}
          onChanged={changed} onError={(t) => setMsg({ kind: 'err', text: t })} />
      ))}
      {d && d.pages > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', marginTop: 12 }}>
          <button type="button" className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span style={{ fontSize: 14 }}>Page {d.page} of {d.pages} ({d.matching} questions)</span>
          <button type="button" className="btn btn-ghost btn-sm" disabled={page >= d.pages} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}
    </div>
  );
}
